import Axios from 'axios';
import { parse } from 'qs';
import { ReserveType } from '@utils/itinerary';
import type FlightPackage from '@itinerary/trip/flight/FlightPackage';
import type HotelPackage from '@itinerary/hotel/HotelPackage';

export function getSmId(reserveType: ReserveType): string | null {
    return LocalStorageUtils.getItemOrNull<string>(
        `${reserveType}-safarmarket-id`
    );
}
export function useSafarMarket() {
    const searchFormStore = useSearchFormStore();
    const { reserveType } = storeToRefs(searchFormStore);
    const formStore = useFormStore();
    const invoiceStore = useInvoiceStore();
    const packageSelectStore = usePackageSelectStore();
    const analyticsStore = useAnalyticsStore();
    const autoReserveStore = useAutoReserveStore();
    const layoutStore = useLayoutStore();

    // private
    function getFlightSmEventUrl(): string | null {
        const smId = getSmId(reserveType.value);
        if (smId) {
            let url = `https://safarmarket.com/api/v1/trace/pixel/mrbilit/$status/?smId=${smId}`;
            const [departurePackage, returnPackage] =
                packageSelectStore.selectedPackages;
            if (departurePackage.haveSmEvent) {
                const fp = departurePackage as FlightPackage;
                url += `&pnr=${invoiceStore.billCode}&fliNo=${
                    fp.segments[0]?.trips[0]?.flightNumber
                }&typ=SYSTEM&pri=${
                    fp.selectedPrice?.singlePayable
                }&pax=${invoiceStore.totalPrice}&adu=${
                    formStore.adultCount
                }&chi=${formStore.childCount}&inf=${
                    formStore.infantCount
                }&from=${fp.originCode}&to=${fp.destinationCode}&dep=${dayConfig
                    .gregory(fp.departureDateTime)
                    .format(formatter({ includeTime: true }))}&nam=${
                    invoiceStore.pax[0].firstName
                } ${invoiceStore.pax[0].lastName}&pho=${
                    invoiceStore.mobile
                }&ema=${invoiceStore.email}`;
                if (returnPackage?.haveSmEvent) {
                    const rfp = departurePackage as FlightPackage;
                    url += `&ret=${dayConfig
                        .gregory(rfp.departureDateTime)
                        .format(
                            formatter({ includeTime: true })
                        )}&rFTyp=SYSTEM&rPnr=${invoiceStore.billCode}&rFliNo=${
                        rfp.segments[0]?.trips[0]?.flightNumber
                    }&bck=false`;
                }
                return url;
            }
            return null;
        } else {
            throw new Error('smId not found!');
        }
    }

    // private
    function getHotelSmEventUrl(): string {
        const hotelPackage = packageSelectStore
            .selectedPackages[0] as HotelPackage;
        const smId = getSmId(reserveType.value);
        if (smId) {
            return `https://safarmarket.com/api/hotel/v1/pixel/mrbilit/$status/0/?
                smId=${smId}
                &DOM=1
                &PAX=${hotelPackage.selectedPrices?.length || 1}
                &ROOMTYPE=${hotelPackage.selectedPricesWithPriceClass?.reduce(
                    (all, [, pt], i) => `${all}${i > 0 ? ',' : ''}${pt.title}`,
                    ''
                )}
                &ROOM=${hotelPackage.selectedPrices?.length || 1}
                &ADL=${hotelPackage.selectedPrices?.length || 1}
                &CHD=0
                &AGES=''
                &DSTID=${hotelPackage.cityId}
                &CTY=${hotelPackage.cityName}
                &CNTRYID=IR
                &HNAME=${hotelPackage.title}
                &HEN=${hotelPackage.engTitle}
                &STAR=${hotelPackage.hotelStar}
                &TOTPR=${invoiceStore.totalPrice / 10}
                &NAME=${invoiceStore.pax[0].persianFirstName} ${
                    invoiceStore.pax[0].persianLastName
                }
                &PHON=${invoiceStore.mobile}
                &EMAIL=${invoiceStore.email}
                &CI=${dayConfig
                    .gregory(hotelPackage.fromDate)
                    .format(formatter())}
                &CO=${dayConfig
                    .gregory(hotelPackage.toDate)
                    .format(formatter())}
                &REF=${invoiceStore.billCode}`;
        } else {
            throw new Error('smId not found!');
        }
    }

    // private
    function getSmEventUrl() {
        switch (reserveType.value) {
            case ReserveType.Flight:
                return getFlightSmEventUrl();
            case ReserveType.Hotel:
                return getHotelSmEventUrl();
            default:
                throw new Error('Method is not implemented!');
        }
    }

    function saveSmEventUrl(): void {
        const smId = getSmId(reserveType.value);
        if (smId) {
            const url = getSmEventUrl();
            if (url) {
                sessionStorage.setItem('safarmarket-url', url);
            }
        }
    }

    // private
    const smInitUrl = computed(() =>
        reserveType.value === ReserveType.Flight
            ? 'https://safarmarket.com/api/v1/trace/pixel/mrbilit/2/?smId=$smId&bck=false'
            : reserveType.value === ReserveType.Hotel
              ? 'https://safarmarket.com/api/hotel/v1/pixel/mrbilit/2/0/?smId=$smId'
              : null
    );

    async function sendSmStatusEvent(status: 2 | 3 | 4 | 5) {
        const smId = getSmId(reserveType.value);
        const url = sessionStorage.getItem('safarmarket-url');
        if (smId && url && !autoReserveStore.isAutoReserve) {
            try {
                await Axios.get(url.replace('$status', status.toString()));
                analyticsStore.trackPosthog(
                    `safarmarket-event-success-${status}`,
                    {
                        url,
                        smId,
                        reserve: reserveType.value,
                    }
                );
            } catch (error) {
                analyticsStore.trackPosthog(
                    `safarmarket-event-failed-${status}`,
                    {
                        url,
                        smId,
                        reserve: reserveType.value,
                    }
                );
                reportException(error);
            }
        } else if (smId && !url) {
            throw new Error(
                'safarmarket id exist but url not found for send event!'
            );
        }
    }

    async function initSm(fullPath: string) {
        const queries = parse(fullPath.split('?')[1], {
            comma: true,
        }) as Record<string, string | string[]>;
        if (
            queries.utm_source === 'safarmarket' &&
            // queries.utm_medium === 'redirection' &&
            queries.safarmarketId
        ) {
            LocalStorageUtils.setItem(
                `${reserveType.value}-safarmarket-id`,
                queries.safarmarketId,
                dayConfig
                    .gregory()
                    .add(7 || 0, 'day')
                    .toDate()
            );
            layoutStore.isRedirectFromSafarmarket = true;
        }

        const smId = (queries.safarmarketId ||
            getSmId(reserveType.value)) as string;
        if (smId && !autoReserveStore.isAutoReserve) {
            const url = smInitUrl.value?.replace('$smId', smId);
            if (!url) {
                throw new Error('SmId not found!');
            }
            try {
                await Axios.get(url);
                analyticsStore.trackPosthog(`safarmarket-event-success-init`, {
                    url,
                    smId,
                    reserve: reserveType.value,
                });
            } catch (error) {
                analyticsStore.trackPosthog(`safarmarket-event-failed-init`, {
                    url,
                    smId,
                    reserve: reserveType.value,
                });
                reportException(error);
            }
        }
    }

    const initSmInFormPage = computed(
        () => reserveType.value === ReserveType.Flight
    );

    return {
        getSmId: () => getSmId(reserveType.value),
        saveSmEventUrl,
        sendSmStatusEvent,
        initSm,
        initSmInFormPage,
    };
}
